import { MainWrapper, ProgressBarContainer } from './slot-details.style'
import { Drawer, Typography, theme } from 'antd'
import { Appointment, AppointmentEventType, CalendarSlot, CalendarSlotTeamMember, CalendarSlotType, Config }  from 'old/types'
import { RightOutlined } from '@ant-design/icons'
import { TranslateFunction, addMinutes, getColorById, useTranslate, useWindowDimensions }  from 'old/utils'
import { ProgressBar } from 'old/components/progress-bar/progress-bar'
import { Avatar } from 'old/components/avatar/avatar'
import { useState } from 'react'
import { useAppointments, useConfig } from 'old/api'
import { ColumnContainer, Label, Loading, PageError } from 'old/components/styled'
import { AppointmentFilter } from 'old/components/appointment/appointment-navigation/appointment-navigation'
import { AppointmentDetails } from 'old/components/appointment-details/appointment-details/appointment-details'
import { AppointmentDetailsHeader } from 'old/components/appointment-details/appointment-details-header/appointment-details-header'
import { AppointmentDetailNavigation } from 'old/components/appointment-details/appointment-details-navigation/appointment-details-navigation'
import { AppointmentDetailsTeamMember } from 'old/components/appointment-details/appointment-details-team-member/appointment-details-team-member'
import { getCustomerName } from 'utils'
const { Text } = Typography

type Props = {
    slot: CalendarSlot
    teamMember: CalendarSlotTeamMember,
    onClose: () => void
}

export const SlotDetails = ({ slot, teamMember, onClose }: Props) => {

    //theme:
    const { token: {
        fontSizeSM,
        paddingXS,
        padding,
        paddingLG,
        marginXXS,
        marginMD,
        marginLG
    }} = theme.useToken()

    //window dimensions:
    const { width, SM } = useWindowDimensions()

    //translate:
    const { __ } = useTranslate()

    //config:
    const { data: config, isLoading: isConfigLoading } = useConfig()

    //color:
    const colorRGB = getColorById(slot.service?.id)

    //appointmentIds:
    const appointmentIds = slot.items.filter(i => i.type == CalendarSlotType.APPOINTMENT).map(i => i.id ?? '')
    const hasAppointments = appointmentIds.length > 0

    //reservedItems:
    const reservedItems = slot.items.filter(i => i.type == CalendarSlotType.RESERVATION)
    const hasReservation = reservedItems.length > 0

    //appointments:
    const { data: appointments, isLoading: isAppointmentsLoading } = useAppointments(
        teamMember.id,
        appointmentIds,
        null,
        AppointmentFilter.ALL,
        null,
        null,
        50,
        hasAppointments,
        false
    )

    //state:
    const [ selectedAppointmentId, setSelectedAppointmentId ] = useState(null as string | null)
    const [ isAppointmentDetailsOpen, setIsAppointmentDetailsOpen ] = useState(false)

    //openInfoDrawer:
    const openInfoDrawer = (appointment: Appointment) => {
        setSelectedAppointmentId(appointment.id)
        setIsAppointmentDetailsOpen(true)
    }

    const calcNumberOfParticipants = (appointments: Appointment[]) => {
        let count = 0
        appointments.map(a => {
            count = count + a.quantity
        })
        return count
    }

    //participantStyle:
    const participantStyle = { padding: `${paddingXS}px ${SM ? paddingLG : padding}px`, borderRadius: 0 }

    //render:
    return (
        (isConfigLoading || !config) ? null : (
            (hasAppointments && (isAppointmentsLoading)) ? <Loading/> : (hasAppointments && !appointments) ? <PageError/> : (

                <MainWrapper style={{ paddingBottom: `${paddingXS}px` }}>

                    <div style={{ padding: `0 ${SM ? paddingXS : 0}px` }}>

                        {/* navigation */}
                        <AppointmentDetailNavigation onClose={onClose}/>
                        
                        {/* header */}
                        <ColumnContainer marginTop={marginXXS}>
                            <AppointmentDetailsHeader
                                title={slot.service?.title ?? __`unknown`}
                                description={getDateTimeRange(slot.dateTime, slot.duration, config)}
                                tag={slot.duration + ' ' + __`min`}
                                colorRGB={colorRGB}
                            />
                        </ColumnContainer>

                    </div>

                    <ColumnContainer marginTop={marginLG}>

                        <div style={{ padding: `0 ${SM ? paddingXS : 0}px` }}>
                            
                            <Text style={{ padding: `0 ${padding}px`, fontFamily: 'inter-medium' }}>
                                {__`participants`}
                            </Text>

                            {/* progress bar */}
                            {slot.service?.groupAppointments &&
                                <ProgressBarContainer>
                                    <ProgressBar
                                        value={appointments ? calcNumberOfParticipants(appointments) : 0}
                                        total={slot.service.groupAppointmentSlots ?? 1}
                                        colorRGB={colorRGB}
                                    />
                                </ProgressBarContainer>
                            }

                        </div>
                        
                        {/* participants */}
                        {hasAppointments && (
                            <ColumnContainer marginTop={marginMD} gap={marginXXS}>
                                {appointments && appointments.map(appointment =>
                                    <Avatar
                                        key={appointment.id}
                                        image={appointment.customer?.image.path ?? null}
                                        name={getCustomerNameAndQuantity(appointment, __)}
                                        tag={getParticipantLabel(getParticipantStatus(appointment), __)}
                                        description={__`customer`}
                                        extraIcon={<RightOutlined style={{ fontSize: fontSizeSM, marginRight: marginXXS }}/>}
                                        style={participantStyle}
                                        onClick={() => openInfoDrawer(appointment)}
                                    />
                                )}
                            </ColumnContainer>
                        )}

                        {/* reservations */}
                        {hasReservation && (
                            <ColumnContainer marginTop={marginMD} gap={marginXXS}>
                                {reservedItems.map(reservation =>
                                    <Avatar
                                        key={reservation.id}
                                        image={null}
                                        name={__`reserved`}
                                        description={__`customer`}
                                        style={participantStyle}
                                    />
                                )}
                            </ColumnContainer>
                        )}

                    </ColumnContainer>

                    <div style={{ padding: `0 ${SM ? paddingXS : 0}px` }}>

                        {/* team member */}
                        {teamMember &&
                            <ColumnContainer marginTop={marginMD}>
                                <AppointmentDetailsTeamMember teamMember={teamMember}/>
                            </ColumnContainer>
                        }

                    </div>

                    {/* info */}
                    <Drawer
                        closable={false}
                        width={SM ? 450 : width} // full width in mobile
                        open={isAppointmentDetailsOpen}
                        styles={{ body: { padding: 0 }}}
                        onClose={() => setIsAppointmentDetailsOpen(false)}
                    >

                        {selectedAppointmentId &&
                            <AppointmentDetails
                                appointmentId={selectedAppointmentId}
                                showTeamMember={false}
                                isOpen={isAppointmentDetailsOpen}
                                onClose={() => setIsAppointmentDetailsOpen(false)}
                            />
                        }
                        
                    </Drawer>

                </MainWrapper>
            )
        )
    )

}

//getDateTimeRange:
export const getDateTimeRange = (start: string, duration: number, config: Config) => {
    const dateTimeFormat = new Intl.DateTimeFormat([config.locale, 'en-US'], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
    const startDate = new Date(start)
    let endDate = new Date(startDate.getTime())
    endDate = addMinutes(endDate, duration)
    return dateTimeFormat.formatRange(startDate, endDate)
}

//getParticipantState:
const getParticipantStatus = (appointment: Appointment): AppointmentEventType | 'CANCELLED' => {
    if(appointment.status === 'cancelled'){
        return 'CANCELLED'
    }else{
        for(let i = appointment.events.length - 1; i >= 0 ; i--){
            if(['NO_SHOW', 'CUSTOMER_CHECKIN', 'RESCHEDULED'].indexOf(appointment.events[i].type) > -1){
                return appointment.events[i].type
            }
        }
        return 'CREATED'
    }
}

//getParticipantLabel:
const getParticipantLabel = (status: AppointmentEventType | 'CANCELLED', __: TranslateFunction) => {
    switch(status){
        case 'NO_SHOW':
            return <Label size='tiny' type='warning' text={__`no_show`}/>
        case 'CUSTOMER_CHECKIN':
            return <Label size='tiny' type='success' text={__`check_in`}/>
        case 'CANCELLED':
            return <Label size='tiny' type='danger' text={__`cancelled`}/>
        default:
            return undefined
    }
}

export const getCustomerNameAndQuantity = (appointment: Appointment,  __: TranslateFunction) => {
    const name = getCustomerName(appointment.customer) ?? __`unknown`
    const quantity = appointment.quantity
    if(quantity === 1){
        return name
    }else{
        return `${name} (${quantity})`
    }
}
